import React, { useState, useEffect } from 'react';
import s3 from '../Clinics/wasabi';
import { useTranslation } from 'react-i18next';

const WasabiImage = ({ bucketName, fileName, alt, width, height }) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    const fetchImage = async () => {
      if (
        bucketName === undefined || bucketName === null || bucketName === '' ||
        fileName === undefined || fileName === null || fileName === ''
      ) {
        setError('Missing required parameters');
        return;
      }
      try {
        const url = await s3.getSignedUrlPromise('getObject', {
          Bucket: bucketName,
          Key: `${fileName}`,
          Expires: 3600,
          ResponseContentDisposition: 'inline'
        });
        setImageUrl(url);
      } catch (err) {
        setError(`Failed to fetch image: ${err.message}`);
      }
    };
    fetchImage();
  }, [bucketName, fileName]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <p>Debug Info:</p>
        <pre>
          {JSON.stringify({
            bucketName,
            fileName
          }, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <div className="banking-image-preview-container" style={{ position: 'relative' }}>
      {imageUrl ? (
        <>
          {/* Fixed Zoom Controls */}
          <div
            className="banking-zoom-controls"
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 10,
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e0e0e0'
            }}
          >
            <button
              className="banking-zoom-btn"
              onClick={handleZoomOut}
              title={t('bankingInfo.zoomOut')}
              disabled={zoomLevel <= 0.5}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '4px',
                background: zoomLevel <= 0.5 ? '#f0f0f0' : '#6c757d',
                color: zoomLevel <= 0.5 ? '#999' : 'white',
                cursor: zoomLevel <= 0.5 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              −
            </button>

            <div
              className="banking-zoom-level"
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#333',
                minWidth: '40px',
                textAlign: 'center'
              }}
            >
              {Math.round(zoomLevel * 100)}%
            </div>

            <button
              className="banking-zoom-btn"
              onClick={handleZoomIn}
              title={t('bankingInfo.zoomIn')}
              disabled={zoomLevel >= 3}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '4px',
                background: zoomLevel >= 3 ? '#f0f0f0' : '#6c757d',
                color: zoomLevel >= 3 ? '#999' : 'white',
                cursor: zoomLevel >= 3 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              +
            </button>

            <button
              className="banking-zoom-btn"
              onClick={handleZoomReset}
              title={t('bankingInfo.resetZoom')}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '4px',
                background: '#6c757d',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                marginLeft: '2px'
              }}
            >
              ⟳
            </button>
          </div>

          {/* Image Container with Scroll */}
          {/* Image Container with Scroll */}
          <div
            style={{
              width: '100%',
              height: '400px',
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
              justifyContent: 'flex-start' // Changed from 'center' to 'flex-start'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: `scale(${zoomLevel})`,
                transformOrigin: '0 0', // Changed from 'center center' to '0 0' (top-left)
                transition: 'transform 0.2s ease',
                minWidth: '100%', // Changed from maxWidth to minWidth
                minHeight: '100%' // Changed from maxHeight to minHeight
              }}
            >
              <img
                src={imageUrl}
                alt={alt || 'Document preview'}
                style={{
                  width: '100%', // Changed from maxWidth to width
                  height: 'auto',
                  display: 'block'
                  // Removed objectFit: 'contain' as it's not needed
                }}
              />
            </div>
          </div>
        </>
      ) : (
        <div
          style={{
            width: '100%',
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            color: '#666'
          }}
        >
          Loading image...
        </div>
      )}
    </div>
  );
};

export default WasabiImage;